/**
 * On-device LLM via Chrome's built-in Prompt API (Gemini Nano).
 *
 * The transcript never leaves the browser. No API key, no server round trip, no
 * per-call cost — the model is already on the user's disk, so unlike our Whisper
 * worker there is nothing to download in the common case.
 *
 * Measured against the live fleet (2026-08-20, 30d of sessions): 85 of 104
 * active users are on Chromium >= 138 desktop, so ~82% get this path. Everyone
 * else falls back to the server, which is why every export here degrades to
 * `unavailable` rather than throwing.
 *
 * WHY FEATURE-DETECTION AND NOT UA-SNIFFING: Edge reports Chrome/151 in its UA
 * but ships a different on-device stack, and enterprise policy can disable the
 * model on a machine that otherwise qualifies. `LanguageModel.availability()`
 * is the only truth.
 */

// ── the slice of the Prompt API we use ───────────────────────────────────────
type Availability = "unavailable" | "downloadable" | "downloading" | "available";

interface LanguageModelSession {
  prompt(input: string, opts?: { responseConstraint?: object; signal?: AbortSignal }): Promise<string>;
  destroy(): void;
  contextWindow?: number;
  contextUsage?: number;
}

interface LanguageModelStatic {
  availability(opts?: object): Promise<Availability>;
  create(opts?: {
    initialPrompts?: Array<{ role: "system" | "user" | "assistant"; content: string }>;
    monitor?: (m: { addEventListener: (e: string, cb: (ev: any) => void) => void }) => void;
    signal?: AbortSignal;
  }): Promise<LanguageModelSession>;
}

function getLanguageModel(): LanguageModelStatic | null {
  if (typeof window === "undefined") return null;
  const lm = (globalThis as any).LanguageModel;
  return lm && typeof lm.availability === "function" ? (lm as LanguageModelStatic) : null;
}

export async function localAiAvailability(): Promise<Availability> {
  const lm = getLanguageModel();
  if (!lm) return "unavailable";
  try {
    return await lm.availability();
  } catch {
    // Availability itself can throw behind enterprise policy — treat as absent.
    return "unavailable";
  }
}

export async function isLocalAiReady(): Promise<boolean> {
  return (await localAiAvailability()) === "available";
}

// ── call-notes schema ────────────────────────────────────────────────────────
/**
 * Constrains Gemini Nano's output. Nano is a small model: given free rein it
 * writes prose and invents fields. `responseConstraint` makes the runtime
 * enforce this shape, which is the difference between parseable output and a
 * coin flip.
 *
 * `additionalProperties: false` matters — without it the model happily invents
 * a `premium` or `carrier` key that no downstream code reads.
 */
export const CALL_NOTES_SCHEMA = {
  type: "object",
  properties: {
    summary: { type: "string", description: "2-4 sentences, what happened on the call" },
    outcome: {
      type: "string",
      enum: ["interested", "not_interested", "callback_requested", "no_answer", "wrong_number", "sold", "undetermined"],
    },
    objections: {
      type: "array",
      items: { type: "string" },
      description: "Concerns raised BY THE PROSPECT only — never the agent's pitch",
    },
    keyDetails: {
      type: "array",
      items: { type: "string" },
      description: "Concrete facts: carriers, premiums, coverage amounts, dates, decisions",
    },
    nextStep: { type: "string", description: "The single concrete next action, or empty" },
    followUpAt: { type: "string", description: "ISO date if a callback time was agreed, else empty" },
    sentiment: { type: "string", enum: ["positive", "neutral", "negative"] },
  },
  required: ["summary", "outcome", "objections", "keyDetails", "nextStep", "followUpAt", "sentiment"],
  additionalProperties: false,
} as const;

export interface CallNotes {
  summary: string;
  outcome: string;
  objections: string[];
  keyDetails: string[];
  nextStep: string;
  followUpAt: string;
  sentiment: string;
}

const SYSTEM_PROMPT = [
  "You take notes on recorded phone calls between a life-insurance agent and a prospect.",
  "Write plainly and factually. Use only what is in the transcript.",
  "If something was not discussed, leave that field empty rather than guessing.",
  "Never invent premiums, carriers, dates, or health details.",
  // ATTRIBUTION. Verified failure 2026-08-21 on a real 44-minute call: the AGENT
  // said "TruStage has a two year waiting period, which I don't want you to
  // have" — his own pitch — and the model recorded it as a PROSPECT objection,
  // while the prospect was in fact denying having that policy at all. An
  // objections list containing the agent's talking points is worse than empty:
  // it invents a concern the customer never had.
  // Attribution, but NOT silence. The first version of this rule was absolute
  // and the model returned ZERO objections on a call that plainly had several
  // (2026-08-21) — an empty list is as useless as a wrong one.
  // Calibration, third attempt. Absolute wording gave ZERO objections; "list
  // every one you find" gave SEVEN, several invented ("used to making money
  // from their file"). Both runs were the same call. Ask for few and real.
  "OBJECTIONS are the real reasons the PROSPECT resisted buying — at most three,",
  "and only ones you can point to in the transcript: price, distrust, timing,",
  "health, or needing to consult someone. Quote or closely paraphrase them.",
  "If the prospect did not resist, return an empty list. Never pad it.",
  "Do NOT list the agent's warnings or carrier comparisons as objections.",
  "Do NOT list facts, circumstances or background as objections.",
  // NOTE-TAKING. The summary alone loses the facts an agent needs on the next
  // call — carrier, premium, coverage, dates, decisions.
  "KEYDETAILS are the concrete facts worth keeping: carrier names, premium amounts,",
  "coverage amounts, beneficiaries, health conditions, effective or mailing dates,",
  "and decisions. One fact per entry.",
  "COPY EVERY NUMBER EXACTLY AS SPOKEN. Never round, correct, combine or reformat a number.",
  "If an amount is garbled or you are unsure of it, leave it out entirely rather than guessing.",
].join(" ");

// Nano's context window is small and transcripts are not. Chunk by characters
// (cheap, no tokenizer in the browser) with the same shape as the 30s audio
// windowing in transcribe.worker.ts, then merge the partial summaries.
const CHUNK_CHARS = 6_000;

function chunk(text: string, size = CHUNK_CHARS): string[] {
  if (text.length <= size) return [text];
  const out: string[] = [];
  for (let i = 0; i < text.length; i += size) out.push(text.slice(i, i + size));
  return out;
}

export interface DraftProgress {
  stage: "checking" | "downloading" | "running" | "merging";
  pct?: number;
  part?: number;
  parts?: number;
}

/**
 * Open a session NOW, synchronously inside a click handler.
 *
 * ⚠️ CHROME REQUIRES A USER GESTURE. Verified headlessly 2026-08-21:
 *
 *   availability = "downloadable"
 *   NotAllowedError: Requires a user gesture when availability is
 *   "downloading" or "downloadable".
 *
 * Transient user activation does not survive `await`. Our chain is
 * click → fetch calls → fetch transcript → TRANSCRIBE (minutes) → create(),
 * so by the time create() ran the gesture was long gone and the very first
 * use on any machine threw. It only appeared to work in testing because that
 * profile already had the model, where create() needs no gesture at all.
 *
 * So the caller must call this as the FIRST statement of the handler, before
 * awaiting anything, and pass the returned promise down. Rejections are
 * swallowed into null — a missing session degrades to the server path.
 */
export function beginLocalAiSession(
  onProgress?: (p: DraftProgress) => void,
): Promise<LanguageModelSession | null> {
  const lm = getLanguageModel();
  if (!lm) return Promise.resolve(null);
  try {
    return lm
      .create({
        initialPrompts: [{ role: "system", content: SYSTEM_PROMPT }],
        monitor: (m) =>
          m.addEventListener("downloadprogress", (e: any) => {
            const pct = e?.total ? Math.round((e.loaded / e.total) * 100) : undefined;
            onProgress?.({ stage: "downloading", pct });
          }),
      })
      .catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
}

/**
 * Draft structured notes from a transcript, entirely on-device.
 * Returns null when the local model is unavailable — callers fall back.
 *
 * Pass `session` from beginLocalAiSession() when the work is user-initiated;
 * creating one here would be too late for a first-run download.
 */
export async function draftCallNotes(
  transcript: string,
  opts: {
    signal?: AbortSignal;
    onProgress?: (p: DraftProgress) => void;
    /** Session opened inside the click — see beginLocalAiSession(). */
    session?: Promise<LanguageModelSession | null> | null;
    /**
     * The prospect's name FROM THE CRM. We already know who this is, so the
     * model must never infer it from audio: on a real call it produced "Montu"
     * for a lead recorded as "Muntu Umoja" (2026-08-21). Names spoken over a
     * phone line are exactly what Whisper mangles most.
     */
    leadName?: string | null;
    /**
     * The agency's own carrier names. Used to snap mangled brand names back to
     * reality — never to invent one that was not mentioned.
     */
    carriers?: string[] | null;
  } = {},
): Promise<CallNotes | null> {
  const lm = getLanguageModel();
  if (!lm) return null;
  const { signal, onProgress } = opts;

  onProgress?.({ stage: "checking" });
  const status = await localAiAvailability();
  if (status === "unavailable") return null;

  let session: LanguageModelSession | null = null;
  try {
    // Prefer the gesture-backed session. Only fall back to creating one here
    // when the model is already downloaded, where no gesture is required.
    session = opts.session ? await opts.session : null;
    if (!session) {
      if (status !== "available") return null; // would throw NotAllowedError
      session = await lm.create({
        initialPrompts: [{ role: "system", content: SYSTEM_PROMPT }],
        signal,
      });
    }

    const parts = chunk(transcript.trim());
    let source = transcript.trim();

    if (parts.length > 1) {
      const partials: string[] = [];
      for (let i = 0; i < parts.length; i++) {
        if (signal?.aborted) return null;
        onProgress?.({ stage: "running", part: i + 1, parts: parts.length });
        const p = await session.prompt(
          `Part ${i + 1} of ${parts.length} of a call transcript. Summarize just this part in 2-3 sentences. Keep carriers, premiums, coverage amounts, dates and commitments VERBATIM, and say WHO said each thing (agent or prospect).\n\n${parts[i]}`,
          { signal },
        );
        partials.push(p.trim());
      }
      onProgress?.({ stage: "merging" });
      source = partials.join("\n\n");
    } else {
      onProgress?.({ stage: "running", part: 1, parts: 1 });
    }

    const who = opts.leadName?.trim()
      ? `The prospect on this call is ${opts.leadName.trim()}. Use that name; never infer a name from the transcript, and do not list the prospect's name as a detail.\n\n`
      : "";
    const raw = await session.prompt(
      `${who}Produce the call notes for this transcript.\n\n${source}`,
      { responseConstraint: CALL_NOTES_SCHEMA, signal },
    );

    // NUMBER GUARD — see stripUnverifiedNumbers(). Runs against the ORIGINAL
    // transcript, not the merged summaries, because the merge is exactly where
    // the fabricated premiums appeared.
    // Constrained decoding should guarantee valid JSON, but a small model plus
    // a runtime bug is not a contract — never let a parse failure surface as a
    // crash inside a notes panel.
    try {
      const parsed = JSON.parse(raw) as CallNotes;
      if (!parsed || typeof parsed.summary !== "string") return null;
      const carriers = opts.carriers || [];
      const fixCarriers = (t: string) => canonicalizeCarriers(t, carriers);
      const rawDetails = (Array.isArray(parsed.keyDetails) ? parsed.keyDetails : []).map(
        (d: string) => fixCarriers(String(d)),
      );
      // The CRM owns the identity. Any "Prospect's name: …" line is a guess from
      // mangled audio and can only be wrong or redundant.
      const withoutNameGuess = rawDetails.filter(
        (d: string) => !/^\s*(prospect(?:'s|s)?|client|customer)\s*(name)\s*[:\-]/i.test(String(d)),
      );
      const { kept, dropped } = stripUnverifiedNumbers(withoutNameGuess, transcript);
      if (dropped.length) {
        // Not silent: a dropped premium is worth knowing about in the console
        // while this is BETA.
        console.warn("[NoteIQ] dropped details with numbers not found in transcript:", dropped);
      }
      return {
        summary: fixCarriers(scrubUnverifiedNumbersInText(parsed.summary ?? "", transcript)),
        outcome: parsed.outcome ?? "undetermined",
        objections: (Array.isArray(parsed.objections) ? parsed.objections : []).map((o: string) => fixCarriers(String(o))),
        keyDetails: kept,
        nextStep: fixCarriers(scrubUnverifiedNumbersInText(parsed.nextStep ?? "", transcript)),
        followUpAt: parsed.followUpAt ?? "",
        sentiment: parsed.sentiment ?? "neutral",
      };
    } catch {
      return null;
    }
  } catch {
    return null;
  } finally {
    try {
      session?.destroy();
    } catch {
      /* session teardown must never break the caller */
    }
  }
}

/**
 * Strip numbers the transcript does not actually contain.
 *
 * ⚠️ MEASURED HALLUCINATION, 2026-08-21, real 44-minute call:
 *   transcript: "their price is 145 56 a month"
 *   model said: "$245.50 per month"          (wrong carrier premium)
 *   model said: "$200.06 per month"          (absent from the transcript)
 *   model said: "$1,360 policy"              (transcript only ever says 136)
 *
 * A wrong premium is the worst error this feature can make — it is the annual
 * premium, and it gets typed into a policy. Prompting did not prevent it, so
 * this verifies every money-like number against the source text and removes any
 * that cannot be found. Deterministic, and it cannot be talked out of.
 *
 * Matching ignores separators because Whisper writes spoken amounts without
 * them ("145 56" for $145.56), so we compare digit sequences only.
 */
const digitsOf = (s: string) => s.replace(/[^0-9]/g, "");

export function stripUnverifiedNumbers(
  values: string[],
  transcript: string,
): { kept: string[]; dropped: string[] } {
  const haystack = digitsOf(transcript);
  const kept: string[] = [];
  const dropped: string[] = [];
  for (const v of values) {
    // Every money-ish token in this line must appear in the transcript.
    const nums = v.match(/\$?\d[\d,.]*/g) || [];
    const bad = nums.some((n) => {
      const d = digitsOf(n);
      // 1-2 digit numbers are ages, counts, "day one" — too noisy to police.
      if (d.length < 3) return false;
      return !haystack.includes(d);
    });
    (bad ? dropped : kept).push(v);
  }
  return { kept, dropped };
}

/**
 * Same verification, applied to free text.
 *
 * The list guard only cleans keyDetails, but the SUMMARY is the field an agent
 * actually reads — and on the 2026-08-21 run it carried the same invented
 * "$245.50/month" straight through. Dropping the whole summary would be worse
 * than the error, so unverifiable money figures are replaced in place with
 * "[amount unclear]": the sentence survives, the wrong number does not.
 */
export function scrubUnverifiedNumbersInText(text: string, transcript: string): string {
  const haystack = digitsOf(transcript);
  return text.replace(/\$\s?\d[\d,.]*/g, (m) => {
    const d = digitsOf(m);
    if (d.length < 3) return m;
    return haystack.includes(d) ? m : "[amount unclear]";
  });
}

/**
 * Snap carrier names to the ones the agency actually writes.
 *
 * Whisper mangles brand names over a phone line, and the model then repeats the
 * mangling confidently. Observed 2026-08-21 on one call: "TrueStage" (TruStage)
 * and "American Homeland Life" (American Home Life). A note that names a carrier
 * that does not exist is worse than one that names none — someone will search
 * for it.
 *
 * We already hold the answer: the agency's own carrier list. So this is a lookup,
 * not a guess — and it only ever replaces a near-match, never invents one.
 *
 * Deliberately conservative: normalized comparison, then containment, then a
 * bounded edit distance. Anything looser starts renaming carriers that were
 * transcribed correctly.
 */
const normalizeCarrier = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "");

function editDistanceWithin(a: string, b: string, max: number): boolean {
  if (Math.abs(a.length - b.length) > max) return false;
  // Classic DP, bounded — these strings are short (carrier names).
  const prev = new Array(b.length + 1);
  const cur = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    let best = cur[0];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      best = Math.min(best, cur[j]);
    }
    if (best > max) return false; // whole row already too far — bail
    for (let j = 0; j <= b.length; j++) prev[j] = cur[j];
  }
  return prev[b.length] <= max;
}

export function canonicalizeCarriers(text: string, carriers: string[]): string {
  if (!text || !carriers.length) return text;
  // Longest first so "American Home Life" wins over a shorter partial.
  const known = [...carriers].filter(Boolean).sort((a, b) => b.length - a.length);
  // Candidate spans: 1-4 capitalised words, allowing lowercase connectors so
  // "Mutual of Omaha" is ONE span. Without that the span stopped at "Mutual",
  // which then matched by prefix and produced "Mutual of Omaha of Omaha".
  return text.replace(
    /\b([A-Z][A-Za-z']+(?:\s+(?:of|and|the|for|de)\s+[A-Z][A-Za-z']+|\s+[A-Z][A-Za-z']+){0,3})\b/g,
    (span) => {
    const n = normalizeCarrier(span);
    if (n.length < 6) return span;
    for (const c of known) {
      const cn = normalizeCarrier(c);
      if (!cn) continue;
      if (cn === n) return c;                       // already right, normalise casing
      // Containment must be near-total. A bare "Mutual" is contained in
      // "mutualofomaha" but is not a carrier reference — requiring the span to
      // be most of the name stops one word from swallowing a longer one.
      if (n.includes(cn)) return c;
      if (cn.includes(n) && n.length >= cn.length * 0.75) return c;
      // PROPORTIONAL, not absolute. A fixed budget was too tight for the real
      // failure: "americanhomelandlife" vs "americanhomelife" is 4 inserted
      // characters — 25% of the name. Scaling by length catches that while
      // keeping "americanamicable" (7 edits away) safely distinct.
      const budget = Math.min(5, Math.ceil(cn.length * 0.25));
      if (editDistanceWithin(n, cn, budget)) return c;
    }
    return span;
    },
  );
}

/**
 * Tidy dictated speech into something worth saving.
 *
 * Deliberately NOT a rewrite. Whisper output is the agent's actual words with
 * poor punctuation and the usual spoken debris ("um", "you know", a repeated
 * clause). Fixing that is a clear win; changing their phrasing is not — an
 * agent who finds their note reworded stops trusting the button.
 *
 * Unconstrained (plain text out, no JSON schema): the whole task is "return
 * this sentence, tidier", and a schema here only invites the model to
 * restructure it.
 */
export async function draftCleanup(raw: string): Promise<string | null> {
  const lm = getLanguageModel();
  if (!lm) return null;
  if ((await localAiAvailability()) !== "available") return null;

  let session: LanguageModelSession | null = null;
  try {
    session = await lm.create({
      initialPrompts: [
        {
          role: "system",
          content: [
            "You clean up dictated text for a life-insurance agent's notes.",
            "Fix punctuation, capitalisation and obvious speech-to-text slips.",
            "Remove filler words and false starts.",
            "KEEP the speaker's own wording, order and meaning. Do not summarise,",
            "reword, add information, or change any name, number or amount.",
            "Return ONLY the cleaned text — no preamble, no quotes, no commentary.",
          ].join(" "),
        },
      ],
    });
    const out = (await session.prompt(`Clean up this dictation:\n\n${raw}`)).trim();
    // A model that "helpfully" answers rather than cleans, or that balloons the
    // text, has misunderstood — fall back to the raw words rather than ship it.
    if (!out || out.length > raw.length * 2.2) return null;
    return out.replace(/^["']|["']$/g, "");
  } catch {
    return null;
  } finally {
    try {
      session?.destroy();
    } catch {
      /* teardown must never throw into the caller */
    }
  }
}

/** Shown wherever NoteIQ output appears, and saved into the note itself. */
export const NOTEIQ_DISCLAIMER = "NoteIQ can make mistakes — check names, amounts and dates.";

/** Render notes as the HTML our rich-note editor stores. */
export function callNotesToHtml(n: CallNotes, opts: { callsUsed?: number } = {}): string {
  const esc = (s: string) =>
    String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const rows: string[] = [`<p>${esc(n.summary)}</p>`];
  // Details first — they are what an agent scans for on the next call.
  if (n.keyDetails.length) {
    rows.push(
      `<p><strong>Details:</strong></p><ul>${n.keyDetails
        .map((d) => `<li>${esc(d)}</li>`)
        .join("")}</ul>`,
    );
  }
  if (n.nextStep) rows.push(`<p><strong>Next step:</strong> ${esc(n.nextStep)}</p>`);
  if (n.followUpAt) rows.push(`<p><strong>Follow up:</strong> ${esc(n.followUpAt)}</p>`);
  if (n.objections.length) {
    rows.push(
      `<p><strong>Objections:</strong></p><ul>${n.objections
        .map((o) => `<li>${esc(o)}</li>`)
        .join("")}</ul>`,
    );
  }
  const source =
    opts.callsUsed && opts.callsUsed > 1 ? `${opts.callsUsed} calls` : "last call";
  // The disclaimer travels WITH the note. Once this is saved it is just a note
  // in the record — whoever reads it next has no idea a model wrote it, and the
  // observed failures are specific: mangled carrier and person names, and
  // invented premiums (2026-08-21).
  rows.push(
    `<p><em>Outcome: ${esc(n.outcome.replace(/_/g, " "))} · sentiment: ${esc(n.sentiment)} · NoteIQ draft from ${esc(source)}</em></p>`,
  );
  rows.push(`<p><em>${esc(NOTEIQ_DISCLAIMER)}</em></p>`);
  return rows.join("");
}
